import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, WheelEvent, Ref } from 'react';
import { createRef, useCallback, useEffect, useRef, useState } from 'react';
import styles from './StoryCards.module.scss';
import { useDraggable } from '../../../hooks/useDraggable';
import gsap from 'gsap';
import useWindowSize from '../../../hooks/useWindowSize';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import StoryCard from 'components/molecules/story-card/StoryCard';
import type { StoryCardsProps, StoryCardsRef } from 'components/blocks/story-cards/StoryCards.data';
import { StoryCardRef } from 'components/molecules/story-card/StoryCard.data';
import useThemeScrollBlock from '@/hooks/useThemeScrollBlock';
import Icon from 'components/atoms/icon/Icon';

export default function StoryCards({
  className,
  blok,
  ref,
}: StoryCardsProps & { ref?: Ref<StoryCardsRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const sliderRef = useRef<HTMLDivElement>(null);
  const progressBarContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const draggableBoundsRef = useRef<HTMLDivElement>(null);
  const draggableInstance = useRef<Draggable | null>(null);

  const itemsRef = useRef(blok.cards.map(() => createRef<StoryCardRef>()));

  const [isLeftArrowDisabled, setLeftArrowDisabled] = useState(true);
  const [isRightArrowDisabled, setRightArrowDisabled] = useState(false);

  const { lock, unLock } = useScrollProviderContext() as ScrollProviderContextType;

  const onDrag = useCallback(() => {
    if (!draggableInstance?.current) return;

    const { x, minX, maxX } = draggableInstance.current;
    const progress = gsap.utils.normalize(maxX, minX, x);

    setLeftArrowDisabled(x >= maxX);
    setRightArrowDisabled(x <= minX);

    if (progressBarRef.current) {
      gsap.set(progressBarRef.current, {
        left: `${100 * progress}%`,
        translateX: `${-100 * progress}%`,
      });
    }
  }, [draggableInstance]);

  draggableInstance.current = useDraggable(
    sliderRef,
    {
      type: 'x',
      edgeResistance: 0.8,
      lockAxis: true,
      inertia: true,
      cursor: 'grab',
      onDrag,
      onThrowUpdate: onDrag,
    },
    {
      bounds: draggableBoundsRef,
    },
  );

  const boundWidth = draggableBoundsRef?.current?.getBoundingClientRect().width || 0;
  const sliderWidth = sliderRef?.current?.getBoundingClientRect().width || 0;

  if (draggableInstance.current) {
    if (boundWidth >= sliderWidth) {
      draggableInstance.current.disable();
    } else {
      draggableInstance.current.enable();
    }
  }

  const resizeProgressBar = () => {
    if (!progressBarRef.current || !progressBarContainerRef.current) return;

    const visibleBounds = draggableBoundsRef?.current?.getBoundingClientRect();
    const sliderBounds = sliderRef?.current?.getBoundingClientRect();

    if (visibleBounds && sliderBounds) {
      gsap.set(progressBarRef.current, {
        width: `${Math.min(1, visibleBounds.width / sliderBounds.width) * 100}%`,
      });
      gsap.set(progressBarContainerRef.current, {
        visibility: visibleBounds.width < sliderBounds.width ? 'visible' : 'hidden',
      });
    }
  };

  const { width: windowWidth } = useWindowSize();

  useEffect(resizeProgressBar, [
    draggableBoundsRef,
    sliderRef,
    progressBarRef,
    progressBarContainerRef,
    windowWidth,
  ]);

  const handleScroll = (e: WheelEvent) => {
    const absDeltaX = Math.abs(e.deltaX);
    const absDeltaY = Math.abs(e.deltaY);
    const isHorizontalDominant = absDeltaX > 0 && absDeltaX > absDeltaY;

    if (isHorizontalDominant) {
      lock();
    } else {
      unLock();
      return;
    }

    if (draggableInstance.current) {
      const { minX, maxX } = draggableInstance.current;
      const currentX: number = parseInt(gsap.getProperty(sliderRef.current, 'x') as string, 10);
      const newX = currentX - e.deltaX;

      const x = Math.min(maxX, Math.max(minX, newX));
      setLeftArrowDisabled(x === maxX);
      setRightArrowDisabled(x === minX);
      gsap.set(sliderRef.current, { x });

      const progress = gsap.utils.normalize(maxX, minX, newX);

      if (progressBarRef.current) {
        gsap.set(progressBarRef.current, {
          left: `${100 * progress}%`,
          translateX: `${-100 * progress}%`,
        });
      }
    }
  };

  const gridGutterRef = useRef<HTMLDivElement>(null);
  const gapBetweenImagesRef = useRef<HTMLDivElement>(null);

  const emptyValues = {
    currentX: 0,
    minX: 0,
    maxX: 0,
    width: 0,
  };
  const getSliderValues = () => {
    if (!itemsRef.current[0]?.current || !draggableInstance.current) return emptyValues;
    const currentX = gsap.getProperty(sliderRef.current, 'x') as number;
    const itemWidth = gsap.getProperty(itemsRef.current[0].current, 'width') as number;

    const gridGapWidth = gsap.getProperty(gapBetweenImagesRef.current, 'width') as number;
    const width = itemWidth + gridGapWidth;
    const { minX, maxX } = draggableInstance.current;

    return { currentX, minX, maxX, width };
  };

  const handleNext = () => {
    const { minX, currentX, width } = getSliderValues();
    const x = Math.max(
      minX,
      (Math.floor(currentX / width) - (currentX % width < 1 ? 1 : 0)) * width,
    );
    setRightArrowDisabled(x === minX);
    setLeftArrowDisabled(false);

    gsap.to(sliderRef.current, { x });
  };

  const handlePrevious = () => {
    const { maxX, currentX, width } = getSliderValues();
    const x = Math.min(
      maxX,
      (Math.ceil(currentX / width) + (currentX % width < 1 ? 1 : 0)) * width,
    );
    setLeftArrowDisabled(x === maxX);
    setRightArrowDisabled(false);

    gsap.to(sliderRef.current, { x });
  };

  const { minX, maxX } = getSliderValues();

  useThemeScrollBlock(elementRef, blok.use_accent_color, {
    foreground: blok.accent_color_foreground?.color || '',
    background: blok.accent_color_background?.color || '',
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.storyCards, className)}
      {...sbEditable(blok)}
    >
      {/* The 2 divs below are here to get --grid-gutter and --grid-gap values in js */}
      <div className={styles.gridGutterWidth} ref={gridGutterRef} />
      <div className={styles.gapBetweenImages} ref={gapBetweenImagesRef} />

      <div className={classNames(styles.title, styles.flexContainer)}>
        {draggableInstance.current && maxX !== minX && (
          <div className={classNames(styles.buttonsContainer)}>
            <button
              className={styles.arrowButton}
              disabled={isLeftArrowDisabled}
              onClick={handlePrevious}
            >
              <Icon name="arrow-left-light" className={classNames(styles.icon)} />
            </button>

            <button
              className={styles.arrowButton}
              disabled={isRightArrowDisabled}
              onClick={handleNext}
            >
              <Icon name="arrow-right-light" className={classNames(styles.icon)} />
            </button>
          </div>
        )}
      </div>

      <div ref={draggableBoundsRef} className={styles.draggableBounds}>
        <div ref={sliderRef} className={styles.slider} onWheel={handleScroll} onMouseLeave={unLock}>
          {blok.cards.map((card, index) => (
            <StoryCard
              key={card._uid}
              card={card}
              className={styles.card}
              ref={itemsRef.current[index]}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
